import { useCallback, useEffect, useMemo, useState } from "react";
import * as React from "react";
import { Button, Typography, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import axios from "axios";
import { apiBase, arrayBufferToBase64, authHeaders } from "@/utils/apiClient";
import { slidePrecedentImageSrcOrNull } from "@/utils/slides/slidePrecedent";
import {
  readActiveJobIds,
  readFrameByJob,
  removeActiveSlidesJob,
} from "@/utils/slides/slidesJobs";
import { useSlideJobProgress } from "@/contentReady/hooks/useSlideJobProgress";
import { useSlideJobPreview } from "@/contentReady/hooks/useSlideJobPreview";
import { logger } from "@/helpers/tslog";

const { Title, Text } = Typography;

const COLOR_PRIMARY = "#FF5C35";
const COLOR_SELECTED = "#1f7d3d";
const COLOR_CARD_BG = "#fafafa";

const SlidesContentReady: React.FC = () => {
  const [jobIds, setJobIds] = useState<string[]>(() => readActiveJobIds());
  const [frameByJob, setFrameByJob] = useState<Record<string, number>>(() => readFrameByJob());
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [completedJobIds, setCompletedJobIds] = useState<Set<string>>(() => new Set());
  const [isInserting, setIsInserting] = useState(false);

  useEffect(() => {
    const refresh = () => {
      setJobIds(readActiveJobIds());
      setFrameByJob(readFrameByJob());
    };
    window.addEventListener("storage", refresh);
    window.addEventListener("message", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("message", refresh);
    };
  }, []);

  useEffect(() => {
    if (selectedJobId && !jobIds.includes(selectedJobId)) {
      setSelectedJobId(null);
    }
  }, [jobIds, selectedJobId]);

  const handleJobCompleted = useCallback((jobId: string) => {
    setCompletedJobIds((prev) => {
      if (prev.has(jobId)) return prev;
      const next = new Set(prev);
      next.add(jobId);
      return next;
    });
  }, []);

  const insertSelected = useCallback(async () => {
    if (!selectedJobId || !apiBase()) return;
    try {
      setIsInserting(true);
      const { data } = await axios.get<ArrayBuffer>(`${apiBase()}/slide-generation/download`, {
        params: { jobId: selectedJobId, type: "ppt" },
        headers: await authHeaders(),
        responseType: "arraybuffer",
        withCredentials: true,
      });
      const base64 = arrayBufferToBase64(data);
      await PowerPoint.run(async (context) => {
        const presentation = context.presentation as any;
        if (typeof presentation.insertSlidesFromBase64 !== "function") {
          throw new Error("This PowerPoint version does not support inserting slides from a generated deck.");
        }
        presentation.insertSlidesFromBase64(base64);
        await context.sync();
      });
      message.success("Generated slide(s) inserted.");
      const insertedJobId = selectedJobId;
      setSelectedJobId(null);
      setCompletedJobIds((prev) => {
        const next = new Set(prev);
        next.delete(insertedJobId);
        return next;
      });
      removeActiveSlidesJob(insertedJobId);
      setJobIds(readActiveJobIds());
      setFrameByJob(readFrameByJob());
    } catch (error) {
      logger.error("[SlidesContentReady] Failed to insert slides", error);
      message.error(error instanceof Error ? error.message : "Failed to insert generated slides.");
    } finally {
      setIsInserting(false);
    }
  }, [selectedJobId]);

  if (jobIds.length === 0) {
    return (
      <div style={{ width: "100%", fontFamily: "Arial, sans-serif" }}>
        <div
          style={{
            maxWidth: 640,
            margin: "0 auto",
            padding: 16,
            display: "flex",
            flexDirection: "column",
            gap: 32,
          }}
        >
          <Title level={5} style={{ color: "black", textAlign: "center" }}>
            You have no active slide generation jobs. You may close the taskpane.
          </Title>
        </div>
      </div>
    );
  }

  const selectedReady = selectedJobId != null && completedJobIds.has(selectedJobId);

  return (
    <div style={{ width: "100%", fontFamily: "Arial, sans-serif", paddingBottom: selectedJobId ? 96 : 0 }}>
      <div
        style={{
          maxWidth: 640,
          margin: "0 auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 32,
        }}
      >
        <Text style={{ marginBottom: 16 }}>
          <span style={{ fontWeight: "bold", color: COLOR_PRIMARY }}>
            Select the generated slide(s) below
          </span>{" "}
          to insert it into your presentation.
        </Text>

        {jobIds.map((jobId, idx) => (
          <SlideJobCard
            key={jobId}
            jobId={jobId}
            indexLabel={idx + 1}
            slideFrameId={frameByJob[jobId] ?? null}
            isSelected={selectedJobId === jobId}
            onSelect={(id) => setSelectedJobId((prev) => (prev === id ? null : id))}
            onJobCompleted={handleJobCompleted}
          />
        ))}
      </div>

      {selectedJobId && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            zIndex: 1000,
            width: "100%",
            backgroundColor: "#fff",
            padding: "24px 36px",
            boxSizing: "border-box",
            display: "flex",
            justifyContent: "center",
            gap: 12,
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <Button
            type="primary"
            disabled={!selectedReady}
            loading={isInserting}
            onClick={insertSelected}
            style={{ backgroundColor: selectedReady ? COLOR_SELECTED : undefined }}
          >
            Insert
          </Button>
          <Button onClick={() => setSelectedJobId(null)}>Cancel</Button>
        </div>
      )}
    </div>
  );
};

export default SlidesContentReady;

interface SlideJobCardProps {
  jobId: string;
  indexLabel: number;
  slideFrameId: number | null;
  isSelected: boolean;
  onSelect: (jobId: string) => void;
  onJobCompleted: (jobId: string) => void;
}

const SlideJobCard: React.FC<SlideJobCardProps> = ({
  jobId,
  indexLabel,
  slideFrameId,
  isSelected,
  onSelect,
  onJobCompleted,
}) => {
  const progress = useSlideJobProgress(jobId);
  const precedentImg = useMemo(() => slidePrecedentImageSrcOrNull(slideFrameId), [slideFrameId]);

  const isComplete = progress?.phase === "completed" || progress?.status === "completed";
  const isFailed = progress?.phase === "failed" || progress?.status === "failed";

  const previewUrl = useSlideJobPreview(jobId, isComplete);

  useEffect(() => {
    if (isComplete) onJobCompleted(jobId);
  }, [isComplete, jobId, onJobCompleted]);

  const borderColor = isSelected ? COLOR_SELECTED : COLOR_PRIMARY;
  const clickable = isComplete && !isFailed;
  const percent = Math.round(progress?.progressPercent ?? 0);
  const stepText = isComplete ? "Complete" : progress?.step || "Preparing...";

  return (
    <div style={{ marginBottom: 32, width: "100%" }}>
      <h3 style={{ fontWeight: 600, marginBottom: 8 }} className="underline text-[15px]">
        Slide(s) {indexLabel}
      </h3>
      <div
        onClick={() => {
          if (!clickable) return;
          onSelect(jobId);
        }}
        style={{
          border: `2px solid ${borderColor}`,
          height: 160,
          width: "100%",
          maxWidth: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 8,
          cursor: clickable ? "pointer" : "not-allowed",
          backgroundColor: COLOR_CARD_BG,
          borderRadius: 4,
          padding: 8,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {isFailed ? (
          <div style={{ textAlign: "center", padding: 8 }}>
            <div style={{ color: "#b91c1c", fontWeight: 600 }}>
              Slide generation failed.
            </div>
            {progress?.step ? (
              <div style={{ color: "#6b7280", fontSize: 12, marginTop: 4 }}>{progress.step}</div>
            ) : null}
          </div>
        ) : isComplete ? (
          previewUrl ? (
            <img
              src={previewUrl}
              alt={`Slide ${indexLabel}`}
              style={{ maxHeight: "100%", maxWidth: "100%" }}
            />
          ) : precedentImg ? (
            <img
              src={precedentImg}
              alt={`Slide ${indexLabel}`}
              style={{ maxHeight: "100%", maxWidth: "100%" }}
            />
          ) : (
            <span style={{ color: "#6b7280", textAlign: "center" }}>
              [Generated slide(s) preview]
            </span>
          )
        ) : (
          <>
            {precedentImg ? (
              <>
                <img
                  src={precedentImg}
                  alt=""
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    opacity: 0.25,
                    filter: "blur(1px)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(255,255,255,0.5)",
                  }}
                  aria-hidden
                />
              </>
            ) : null}
            <div style={{ position: "relative", width: "100%", textAlign: "center", padding: 8 }}>
              <div
                style={{
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                <LoadingOutlined style={{ color: COLOR_PRIMARY }} spin />
                <Text style={{ fontSize: 16 }}>{percent}% Complete</Text>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Text style={{ fontSize: 16 }}>
                  <span style={{ fontWeight: 600 }}>Step:</span> {stepText}
                </Text>
              </div>
            </div>
          </>
        )}
      </div>
      <p style={{ fontSize: 14, fontWeight: 500 }}>Share Price Performance || Slide(s)</p>
    </div>
  );
};
